import { format } from 'date-fns';

import { retintEventBackground, retintEventForeground, type ResolvedTheme } from '../theme';
import type { Rhythm, RhythmPeriod } from './types';

export const SWATCHES = [
  '#4caf7a',
  '#2d8a8e',
  '#4a90c0',
  '#3a6fb0',
  '#5a6ad0',
  '#8a6fd1',
  '#b05aa0',
  '#e0507a',
  '#c0504a',
  '#e0773a',
  '#d1a03a',
  '#6a7f3a'
];

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const PERIOD_LABEL: Record<RhythmPeriod, string> = {
  day: 'day',
  week: 'week',
  fortnight: '2 weeks',
  month: 'month'
};

export function tagMatches(taskTags: string[], wanted: string[]): boolean {
  return wanted.some((w) => {
    const t0 = w.toLowerCase().replace(/^#/, '');
    return taskTags.some((t) => t === t0 || t.startsWith(t0 + '/'));
  });
}

export interface Tint {
  solid: string;
  fg: string;
  soft: string;
  softer: string;
  line: string;
}

// Rhythm colours run through the same per-theme retint as calendar colours,
// then get mixed toward the page background for the lighter states.
export function tint(hex: string | null | undefined, theme: ResolvedTheme): Tint {
  const solid = retintEventBackground(hex ?? null, theme);
  return {
    solid,
    fg: retintEventForeground(hex ?? null, theme),
    soft: `color-mix(in srgb, ${solid} 24%, var(--bg))`,
    softer: `color-mix(in srgb, ${solid} 10%, var(--bg))`,
    line: `color-mix(in srgb, ${solid} 70%, var(--bg))`
  };
}

export function minutesLabel(m: number): string {
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

export function hoursLabel(m: number): string {
  const h = m / 60;
  return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1).replace(/\.0$/, '')}h`;
}

export function timeRange(start: Date, end: Date): string {
  const sa = format(start, 'a').toLowerCase();
  const ea = format(end, 'a').toLowerCase();
  const s = sa === ea ? format(start, 'h:mm') : `${format(start, 'h:mm')}${sa}`;
  return `${s} – ${format(end, 'h:mm')}${ea}`;
}

export function clockLabel(d: Date): string {
  return format(d, 'h:mmaaa').replace(':00', '');
}

export function cadenceLabel(r: Rhythm): string {
  if (r.kind === 'hours') return `${r.target}h a ${PERIOD_LABEL[r.period]}`;
  if (r.period === 'day') return r.target === 1 ? 'Daily' : `${r.target}× a day`;
  if (r.target === 1) {
    return r.period === 'fortnight' ? 'Every 2 weeks' : `Once a ${PERIOD_LABEL[r.period]}`;
  }
  return `${r.target}× a ${PERIOD_LABEL[r.period]}`;
}

export function minuteOfDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

export function hm(s: string): number {
  const [h, m] = s.split(':').map(Number);
  return h * 60 + m;
}
